import React from 'react'
import { Button, ButtonStyle } from './button'
import { IconAngleRight } from '../../icons/library'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuPortal,
  DropdownMenuSubTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
} from '@radix-ui/react-dropdown-menu'

export const dropdownBaseStyle = 'flex items-center gap-2 rounded px-2 py-1 focus:outline-none w-full'
export const dropdownItemStyle = `cursor-pointer hover:bg-slate-100 focus:bg-slate-100 active:bg-slate-200 data-[highlighted]:bg-slate-100 data-[active]:bg-slate-100 ${dropdownBaseStyle}`
export const dropdownMenuStyle =
  'z-[2000] flex min-w-full flex-col gap-1 overflow-y-auto rounded-lg border border-gray-100 bg-white p-1 shadow-md'

interface DropdownProps {
  /** The trigger button for the dropdown, or a function that returns a button based on the open state */
  button: ((open: boolean) => React.ReactNode) | React.ReactNode
  /** The content of the dropdown, or a function that returns content based on the open state */
  children: ((open: boolean) => React.ReactNode) | React.ReactNode
  /** The offset for the dropdown menu from the trigger in pixels */
  offset?: number
  /** Call\back when the dropdown is opened or closed */
  onOpenChange?: (open: boolean) => void
}

/**
 * A single item in the dropdown menu. It can be used as a child of Dropdown.
 */
export const DropdownItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuItem>,
  React.ComponentProps<typeof DropdownMenuItem>
>((props, ref) => {
  return <DropdownMenuItem {...props} ref={ref} className={`${dropdownItemStyle} ${props.className || ''}`} />
})

export const DropdownSubmenu: React.FC<DropdownProps> = ({ button, children, offset = 2 }) => {
  const [open, setOpen] = React.useState(false)

  return (
    <DropdownMenuSub onOpenChange={setOpen} open={open}>
      <DropdownMenuSubTrigger asChild>{typeof button === 'function' ? button(open) : button}</DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent
          collisionPadding={8}
          sideOffset={offset}
          className={`max-h-[300px] ${dropdownMenuStyle}`}
        >
          {typeof children === 'function' ? children(open) : children}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  )
}

/**
 * A dropdown menu that can be used to display a list of items.
 */
export const Dropdown: React.FC<DropdownProps> = ({ button, children, onOpenChange, offset = 3.5, ...props }) => {
  const [open, setOpen] = React.useState(false)

  return (
    <DropdownMenu
      onOpenChange={(o) => {
        onOpenChange && onOpenChange(o)
        setOpen(o)
      }}
      open={open}
      {...props}
    >
      <DropdownMenuTrigger asChild>{typeof button === 'function' ? button(open) : button}</DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent
          collisionPadding={8}
          sideOffset={offset}
          align="start"
          className={`max-h-[300px] ${dropdownMenuStyle}`}
        >
          {typeof children === 'function' ? children(open) : children}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  )
}

interface SelectProps<T = any> extends Omit<DropdownProps, 'button'> {
  options: { name: string; value: T }[]
  onSelect: (value: T) => void
}

/**
 * A dropdown menu that displays a list of options and allows the user to select one.
 */
export const SelectDropdown: React.FC<SelectProps> = ({ children, options, onSelect, ...props }) => {
  return (
    <Dropdown button={children} {...props}>
      {options.map((option) => (
        <DropdownItem key={option.value} onSelect={() => onSelect(option.value)} className="text-left">
          {option.name}
        </DropdownItem>
      ))}
    </Dropdown>
  )
}

interface ArrowSelectProps<T = any> extends SelectProps<T> {
  buttonStyle?: ButtonStyle
  className?: string
}

/**
 * A dropdown menu that displays a list of options and allows the user to select one.
 * It also shows an arrow icon to indicate the open state.
 */
export const ArrowSelectDropdown: React.FC<ArrowSelectProps> = ({ buttonStyle, className, children, ...props }) => {
  return (
    <SelectDropdown {...props}>
      {(open) => (
        <Button buttonStyle={buttonStyle} className={`${className}`}>
          {typeof children === 'function' ? children(open) : children}
          <IconAngleRight
            className={`-mr-2 ml-2 h-3 w-3 transition-transform duration-75 ${open ? 'rotate-90 transform' : ''}`}
            thicken={15}
          />
        </Button>
      )}
    </SelectDropdown>
  )
}

interface DropdownDividerProps {
  full?: boolean
  className?: string
}

/**
 * A divider for the dropdown menu. It can be used to separate items in the menu.
 */
export const DropdownDivider: React.FC<DropdownDividerProps> = ({ full, className }) => {
  return <div className={`my-0 border-t border-gray-100 ${full ? '-mx-1' : 'mx-1'} ${className}`} />
}
interface DropdownLabelProps {
  children: React.ReactNode
}

/**
 * A label for the dropdown menu. A label is a text that describes the content of the dropdown.
 */
export const DropdownLabel: React.FC<DropdownLabelProps> = ({ children }) => {
  return <div className="px-2 py-1 text-sm font-bold text-gray-500">{children}</div>
}
